/* A building level and a colony are orders that land when the round turns (owner, 2026-10-02:
   *"building upgrades have to be processed in the next round (so only 1 level per building per
   round)"*, and *"This applies also for starting a colony"*).

   The server keeps them in `metadata.upgrades` and `metadata.foundings` until `process_round`
   prices, pays and lands them, and sends them only to the orderer's team (`games/visibility`,
   mirrored in `fog.js`). This is the one client reader of the two lists, mirroring what
   `level_up_building` and `found_settlement` check at the order:

   - one level a building a round (`BuildingAlreadyUpgrading`), one colony a cell
     (`SettlementAlreadyOrdered`);
   - the price is held against the orders already given: humanity's levels against the workers of
     the same city, the horde's levels and colonies against its one meat bank.

   Nothing here raises a level or moves a deed early: the board the client draws is the board the
   server holds, and the pending order is drawn as pending.

   **And the player sees only what is free** (owner, 2026-10-02: *"I don't want the workers that are
   in constructions nor the meat visible in the UI. Let's just show the effective stuff. It's just
   there for the enemies in case they check."*). A pending order HOLDS its price until the round
   turns — humanity's levels the workers of their city, humanity's colonies the workers on their
   cell, the horde's levels and colonies its one meat bank — and the server prices every other
   spend against what is left (`simulator/use_cases/holds.py`, mirrored here). `effectiveForPlayer`
   takes the holds off the player's own numbers in the game every panel reads (`selectGame`), so
   the top bar, the sheets, the roster and every gate quote the free amounts and nothing has to
   subtract a hold a second time. The raw numbers stay in the store for the socket to merge into,
   and are what an enemy reading the cell sees.

   **A discharge is an order too** (owner, 2026-10-23: *"humanos dar de baja Tropas sucede la
   próxima ronda"*, requirement 36 D): `metadata.discharges` holds humanity's units ordered to
   stand down, one entry per player per cell, and they are held where they stand until the round
   turns (`holds.held_units`). `effectiveForPlayer` takes them off the player's own garrison on
   that cell, so the march picker, the tunnel sheet and the retire sheet offer what the server
   accepts; `dischargeOrderedAt` is the one reader of the list for the pick on the map, the roster
   row and the city sheet. */
import { upgradeCostFor } from "src/utils/buildingCosts"

/* What the next colony costs, mirroring `found_settlement.settlement_cost`: the base price
   MULTIPLIED by `SETTLEMENT_GROWTH` for every colony already held, snapped to the owner's steps
   of fifty and never under the first colony's price. It was a flat surcharge per colony until
   2026-09-22 — a shape that made the next colony cheaper in real terms the bigger the empire got,
   which is requirement 34's bug in the one price that ladder did not reach. */
const settlementPrice = (base, growth, colonies) =>
  Math.max(base, 50 * roundHalfEven((base * growth ** colonies) / 50))

/* Python's `round()`, which the server snaps the price with: a half goes to the even neighbour,
   where `Math.round` sends it up. With the shipped constants the two agree by luck (every exact
   half lands on an even number); at `SETTLEMENT_COST_WORKERS = 100` the third colony is 200 on
   the server and would be 250 here. */
export const roundHalfEven = (value) => {
  const floor = Math.floor(value)
  const fraction = value - floor
  if (fraction < 0.5) return floor
  if (fraction > 0.5) return floor + 1
  return floor % 2 === 0 ? floor : floor + 1
}

/* The three cities a player started on (requirement 35, M4): `starting_location_list`. On S and
   M only the first of them are crowns (`initial_location_list`), but every one of the three is a
   starting city and none is a colony. A snapshot from before the sizes has only
   `initial_location_list`, which was all three. */
export const startingCitiesOf = (userMetadata) =>
  userMetadata?.starting_location_list || userMetadata?.initial_location_list || []

/* A colony is any owned cell that is not one of the player's starting cities. `cells` is the raw
   metadata dict keyed "col-row", owners are user ids; `initialLocations` is the starting cities,
   `[[col,row],...]` (`startingCitiesOf`, never the crowns alone). */
export const coloniesHeld = ({ cells, userId, initialLocations }) => {
  const crowns = new Set((initialLocations || []).map((c) => `${c[0]}-${c[1]}`))
  let colonies = 0
  for (const [key, cell] of Object.entries(cells || {})) {
    if (String(cell?.owner) === String(userId) && !crowns.has(key)) colonies += 1
  }
  return colonies
}

export const colonyPriceAt = (rules, team, colonies) =>
  team === "human"
    ? settlementPrice(rules?.SETTLEMENT_COST_WORKERS ?? 0, rules?.SETTLEMENT_GROWTH ?? 1, colonies)
    : settlementPrice(rules?.SETTLEMENT_COST ?? 0, rules?.SETTLEMENT_GROWTH ?? 1, colonies)

const at = (order, col, row) =>
  Number(order?.coordinates?.[0]) === Number(col) && Number(order?.coordinates?.[1]) === Number(row)

const mine = (order, userId) => String(order?.user_id) === String(userId)

export const upgradeOrderedAt = ({ metadata, userId, col, row, buildingCode }) =>
  (metadata?.upgrades || []).some(
    (order) => mine(order, userId) && at(order, col, row) && order.building === buildingCode
  )

export const foundingOrderedAt = ({ metadata, col, row }) =>
  (metadata?.foundings || []).find((order) => at(order, col, row)) || null

export const pendingFoundingsOf = ({ metadata, userId }) =>
  (metadata?.foundings || []).filter((order) => mine(order, userId)).length

/* This player's discharge ordered on (col, row) this round, as the server keeps it in
   `metadata.discharges` — `{user_id, coordinates, troops: {unit: n}, created_tick}`, one per
   player per cell — or null. The one reader the map's pick, the roster row and the city sheet
   share. */
export const dischargeOrderedAt = ({ metadata, userId, col, row }) =>
  (metadata?.discharges || []).find((order) => mine(order, userId) && at(order, col, row)) || null

/* What a pending discharge retires, as bodies, and what it gives (`discharge_units.workers_from`,
   off the published `biological_units` × `DISCHARGE_WORKERS_PER_BODY`). */
export const dischargeSummary = ({ order, gameConfig }) => {
  const perBody = gameConfig?.human_config?.DISCHARGE_WORKERS_PER_BODY ?? 0
  const flesh = new Set(gameConfig?.biological_units || [])
  let bodies = 0
  let workers = 0
  Object.entries(order?.troops || {}).forEach(([type, number]) => {
    bodies += Number(number) || 0
    if (flesh.has(type)) workers += (Number(number) || 0) * perBody
  })
  return { bodies, workers }
}

/* What this player's levels already ordered will take from the purse a level at (col, row) pays
   from: that city's workers for humanity, the whole meat bank for the horde. */
export const heldByUpgrades = ({ metadata, gameConfig, userId, team, race, col, row }) =>
  (metadata?.upgrades || [])
    .filter((order) => mine(order, userId) && (team !== "human" || at(order, col, row)))
    .reduce((sum, order) => {
      const [c, r] = order.coordinates || []
      const level = metadata?.cells?.[`${c}-${r}`]?.metadata_by_user?.[String(userId)]?.buildings?.[order.building]?.level ?? 0
      return sum + (upgradeCostFor({ gameConfig, team, buildingCode: order.building, level, race }) ?? 0)
    }, 0)

/* What this player's colonies ordered this round hold, each at the price it was ordered at (the
   first at the colonies held now, the next one step dearer), mirroring
   `found_settlement.held_by_foundings`: the horde's are meat from its one bank, so every one
   counts; humanity's are the workers on the colony's own cell, so only the one at (col, row). */
export const heldByFoundings = ({ metadata, rules, userId, team, col, row }) => {
  const held = coloniesHeld({
    cells: metadata?.cells,
    userId,
    initialLocations: startingCitiesOf(metadata?.users?.[String(userId)]),
  })
  return (metadata?.foundings || [])
    .filter((order) => mine(order, userId))
    .reduce(
      (sum, order, i) => (team !== "human" || at(order, col, row) ? sum + colonyPriceAt(rules, team, held + i) : sum),
      0
    )
}

/* The bank this player's pending orders hold (`holds.held_bank`): the horde's levels and
   colonies; nothing for humanity, none of whose orders pays in food. */
export const heldBank = ({ metadata, gameConfig, userId }) => {
  const user = metadata?.users?.[String(userId)]
  if (!user || user.team === "human") return 0
  const team = user.team
  return (
    heldByUpgrades({ metadata, gameConfig, userId, team, race: user.race }) +
    heldByFoundings({ metadata, rules: gameConfig?.rules_config, userId, team })
  )
}

/* The workers on (col, row) this player's pending orders hold (`holds.held_workers`): humanity's
   levels at that city and its colony on that cell. */
export const heldWorkersAt = ({ metadata, gameConfig, userId, col, row }) => {
  const user = metadata?.users?.[String(userId)]
  if (!user || user.team !== "human") return 0
  return (
    heldByUpgrades({ metadata, gameConfig, userId, team: "human", race: user.race, col, row }) +
    heldByFoundings({ metadata, rules: gameConfig?.rules_config, userId, team: "human", col, row })
  )
}

/* The game as its player sees it: their own bank and their own workers with what their pending
   orders hold already taken off, and their own garrisons without the units a tunnel entry or a
   discharge holds. Returns the same object when nothing is held, so a memoised selector stays
   stable. Only the player's OWN numbers move — an ally's view of them is raw, the way the holds
   are per player on the server. */
export const effectiveForPlayer = ({ game, gameConfig, userId }) =>
  withDischargeHolds({ game: withTunnelHolds({ game: withPriceHolds({ game, gameConfig, userId }), userId }), userId })

/* This player's tunnel orders of this round (owner, 2026-10-05), as the server keeps them in
   `metadata.tunnel_orders`: `{id, user_id, coordinates, direction: "load"|"unload", units}`. */
export const tunnelOrdersOf = ({ metadata, userId }) =>
  (metadata?.tunnel_orders || []).filter((order) => mine(order, userId))

/* The units a pending tunnel order holds (`tunnel.held_in_garrison` / `held_in_network`): an entry
   holds them in the garrison it leaves from, an exit in the network. Taken off the player's own
   numbers, as the price holds are, so the garrison and the network show what may still be ordered
   and every picker offers no more than the server accepts. */
const minus = (units, held) => {
  let changed = false
  const next = { ...units }
  Object.entries(held).forEach(([type, number]) => {
    const unit = next[type]
    if (!unit || !number) return
    changed = true
    const left = Math.max(0, Number(unit.number ?? 0) - number)
    if (left === 0) delete next[type]
    else next[type] = { ...unit, number: left }
  })
  return changed ? next : units
}

const add = (into, units) => {
  Object.entries(units || {}).forEach(([type, number]) => {
    into[type] = (into[type] || 0) + Number(number || 0)
  })
  return into
}

/* Take `heldByCell` ({"col-row": {unit: n}}) off this player's garrisons, sharing nothing with the
   raw payload that did not change. */
const withoutHeldUnits = ({ game, userId, heldByCell }) => {
  const metadata = game?.metadata
  let cells = metadata.cells
  Object.entries(heldByCell).forEach(([key, held]) => {
    const side = cells?.[key]?.metadata_by_user?.[String(userId)]
    if (!side) return
    const units = minus(side.units || {}, held)
    if (units === side.units) return
    if (cells === metadata.cells) cells = { ...cells }
    cells[key] = {
      ...cells[key],
      metadata_by_user: { ...cells[key].metadata_by_user, [String(userId)]: { ...side, units } },
    }
  })
  return cells === metadata.cells ? game : { ...game, metadata: { ...metadata, cells } }
}

const withTunnelHolds = ({ game, userId }) => {
  const metadata = game?.metadata
  const orders = userId == null ? [] : tunnelOrdersOf({ metadata, userId })
  if (orders.length === 0) return game
  const user = metadata.users?.[String(userId)]
  if (!user) return game

  const outOfNetwork = orders.filter((o) => o.direction === "unload").reduce((held, o) => add(held, o.units), {})
  const tunnel = minus(user.tunnel || {}, outOfNetwork)
  const users = tunnel === user.tunnel ? metadata.users : { ...metadata.users, [String(userId)]: { ...user, tunnel } }
  const withNetwork = users === metadata.users ? game : { ...game, metadata: { ...metadata, users } }

  const intoNetwork = {}
  orders
    .filter((o) => o.direction === "load")
    .forEach((o) => {
      const key = `${o.coordinates?.[0]}-${o.coordinates?.[1]}`
      intoNetwork[key] = add(intoNetwork[key] || {}, o.units)
    })
  return withoutHeldUnits({ game: withNetwork, userId, heldByCell: intoNetwork })
}

/* The units a pending discharge holds (`holds.held_units`, owner 2026-10-23): they stand in the
   garrison until the round turns and are workers then, so the player's own garrison on that cell
   reads without them. */
const withDischargeHolds = ({ game, userId }) => {
  const metadata = game?.metadata
  const orders = userId == null ? [] : (metadata?.discharges || []).filter((order) => mine(order, userId))
  if (orders.length === 0) return game
  if (!metadata.users?.[String(userId)]) return game
  const heldByCell = {}
  orders.forEach((o) => {
    const key = `${o.coordinates?.[0]}-${o.coordinates?.[1]}`
    heldByCell[key] = add(heldByCell[key] || {}, o.troops)
  })
  return withoutHeldUnits({ game, userId, heldByCell })
}

const withPriceHolds = ({ game, gameConfig, userId }) => {
  const metadata = game?.metadata
  const user = userId == null ? null : metadata?.users?.[String(userId)]
  if (!user) return game
  const orders = [...(metadata.upgrades || []), ...(metadata.foundings || [])].filter((order) => mine(order, userId))
  if (orders.length === 0) return game

  const bank = heldBank({ metadata, gameConfig, userId })
  const users = bank
    ? { ...metadata.users, [String(userId)]: { ...user, food: Math.max(0, Number(user.food ?? 0) - bank) } }
    : metadata.users

  let cells = metadata.cells
  let heldWorkers = 0
  const places = new Set(orders.map((order) => `${order.coordinates?.[0]}-${order.coordinates?.[1]}`))
  for (const key of places) {
    const [col, row] = key.split("-").map(Number)
    const held = heldWorkersAt({ metadata, gameConfig, userId, col, row })
    const side = cells?.[key]?.metadata_by_user?.[String(userId)]
    const workers = side?.units?.worker
    if (!held || !workers) continue
    heldWorkers += Math.min(held, Number(workers.number ?? 0))
    if (cells === metadata.cells) cells = { ...cells }
    cells[key] = {
      ...cells[key],
      metadata_by_user: {
        ...cells[key].metadata_by_user,
        [String(userId)]: {
          ...side,
          units: { ...side.units, worker: { ...workers, number: Math.max(0, Number(workers.number ?? 0) - held) } },
        },
      },
    }
  }
  // The census the roster's header sums (`units_per_user`) counts the same workers the rows do,
  // or the header reads the held ones over what the rows add up to.
  let census = metadata.units_per_user
  const counted = census?.[String(userId)]?.worker
  if (heldWorkers && counted != null) {
    census = { ...census, [String(userId)]: { ...census[String(userId)], worker: Math.max(0, Number(counted) - heldWorkers) } }
  }
  if (users === metadata.users && cells === metadata.cells && census === metadata.units_per_user) return game
  return { ...game, metadata: { ...metadata, users, cells, units_per_user: census } }
}

/* The `upgradeGate` input a pending order changes: whether this building already has its level
   for the round. What the other orders hold is not passed: the numbers the gate is handed are
   already the free ones (`effectiveForPlayer`). */
export const upgradeOrders = ({ metadata, userId, col, row, buildingCode }) => ({
  ordered: upgradeOrderedAt({ metadata, userId, col, row, buildingCode }),
})
